// NJORD — ANTATT wire-kontrakt mot KOP-broen. MIDLERTIDIG HJEM.
//
// ══ LES DETTE FØRST ═════════════════════════════════════════════════════════
// Disse typene hører IKKE hjemme her. De skal inn i `@skade/contracts` som
// `kop.ts` (kystobservasjonspost), fordi NJORD, SKADE-hubben, COP og KOP-broen
// alle må lese det SAMME formatet — og en kopi av et wire-format i en klient er
// nettopp divergensen grunnloven §3 forbyr. De ligger lokalt bare fordi
// kontrakten ikke er skrevet ennå, og de ligger i ÉN fil så flyttingen blir et
// klipp-og-lim og ikke en arkeologisk utgraving.
//
// Det som ALLEREDE finnes i contracts gjenbrukes og defineres ikke på nytt:
//   * `Affiliation` + `Track`  (track.ts)      — utgangen mot SKADE
//   * `RadarInfo`/`RadarSpoke` (radar.ts)      — radarbroens format
//   * `polarTarget`            (targeting.ts)  — peiling+avstand → MGRS
//   * SIDC-konvensjonen for sjøoverflate       — se `aisToTracks` i ais.ts
//
// ══ DESIGNVALGET SOM BÆRER HELE APPEN ══════════════════════════════════════
// `Maaling` er en DISKRIMINERT UNION, ikke ett objekt med femten valgfrie felt.
// Grunnen er operativ, ikke estetisk: kontaktkortet skal vise HVA sensoren
// faktisk målte. Med valgfrie felt kan en «radar-observasjon» eksistere uten
// avstand og en «RF-observasjon» uten frekvens, og da må UI-et gjette — og
// gjetningen ser ut som en måling. Med unionen KAN en frekvensmåling ikke
// mangle frekvensen, og en peiling uten avstand er en egen ting med eget navn.
// En kontakt sett av kun én peiling er en helt annen sak enn en krysspeilt
// kontakt, og det skillet skal være umulig å miste på veien.
//
// ══ ØVINGSFLAGGET: HVORFOR DET STÅR PÅ HVER TYPE OG IKKE BARE ØVERST ════════
// `ovelse?: true` (fra `@skade/sensorflate`) er arvet inn i post, sensor,
// observasjon OG kontakt. Det ser ut som støy, og det er det ikke: kravet er at
// øvingsdata skal være umulig å forveksle med ekte data, og et flagg som bare
// finnes på toppnivået forsvinner i det øyeblikket noe plukker ut EN kontakt
// eller EN observasjon og viser den alene — i et kontaktkort, i en logglinje, i
// en `Track` sendt til SKADE. Da er flagget ikke i dataen, det er i skjermbildet
// den kom fra, og det er nettopp forskjellen §1 handler om.
//
// Flagget settes ALDRI for hånd her. Øvingskilden stempler hver melding
// (`lagOvelseskilde`), og `lib/lager.ts` bærer stempelet videre ned på
// entitetene. Se `api/ovelse.ts`.
import type { Affiliation, Track } from '@skade/contracts';
import type { MeldtTilstand, Ovelsemerket } from '@skade/sensorflate';

// ── Sensorene på posten ─────────────────────────────────────────────────────

/**
 * De fire sensorartene en kystobservasjonspost er bygget av. Lista er lukket
 * med vilje: hver art har sin egen MÅLETYPE og sin egen måte å svikte på, og
 * en «annet»-verdi ville blitt en skuff der ukjente sensorer havnet uten at
 * noen bestemte hvordan de skulle leses.
 */
export type Sensorart = 'radar' | 'rf' | 'hydrofon' | 'kamera';

export const SENSORART_NAVN: Record<Sensorart, string> = {
  radar: 'Radar',
  rf: 'RF',
  hydrofon: 'Hydrofon',
  kamera: 'Kamera',
};

/** Hva arten faktisk måler — brukes i tomtilstander og i kontaktkortet. */
export const SENSORART_MAALER: Record<Sensorart, string> = {
  radar: 'peiling og avstand',
  rf: 'peiling og frekvens',
  hydrofon: 'peiling og lydsignatur',
  kamera: 'bilde',
};

/**
 * Broens egen vurdering av sensoren. `ukjent` = broen sa ingenting.
 *
 * Definert i `@skade/sensorflate` og bare gjenbrukt her: ordforrådet
 * ok/degradert/feil/av/ukjent er det friskhetsmodellen regner på, og det er det
 * samme enten broen melder om en hydrofon eller en brytertråd. Re-eksporteres
 * fordi det er en del av KOP-wire-formatet slik resten av appen leser det.
 */
export type { MeldtTilstand };

export interface KopSensor extends Ovelsemerket {
  id: string;
  art: Sensorart;
  /** Menneskelesbart driftsnavn, f.eks. «Radar Nord». Settes i drift, ikke i kode. */
  navn: string;
  /** Hvilken post sensoren står på. NJORD viser ÉN post; SKADE viser alle. */
  postId: string;
  /** Sensorens plassering. Mangler den, kan ikke peilinger projiseres i kartet. */
  lat?: number;
  lon?: number;
  /** Der sensoren peker nå (grader sann) — meningsfullt for PTZ og retningsantenne. */
  peilingDeg?: number;
  /** Åpningsvinkel i grader (kamera-FOV, antenne-lobe), om broen oppgir den. */
  apningDeg?: number;
  /** Rekkevidde slik DRIFTEN har oppgitt den. Aldri hentet fra datablad. */
  rekkeviddeM?: number;
  tilstand: MeldtTilstand;
  /**
   * Broens klartekst-forklaring på en degradert/feilet sensor. Vises ORDRETT
   * til operatøren — en oversettelse til vår egen ordliste ville skjult hva
   * som faktisk er galt.
   */
  merknad?: string;
  /**
   * Hvor ofte sensoren normalt melder (ms). Radaren snurrer, hydrofonen
   * strømmer, RF-en sveiper — «stille» betyr ikke det samme for alle. Mangler
   * verdien, brukes standardtersklene i `@skade/sensorflate` (`friskhet.ts`).
   */
  forventetIntervallMs?: number;
}

// ── Målingen: hva sensoren FAKTISK målte ────────────────────────────────────

export type Maalingslag = 'peiling' | 'peiling-avstand' | 'frekvens' | 'lydsignatur' | 'bilde';

export const MAALINGSLAG_NAVN: Record<Maalingslag, string> = {
  peiling: 'Peiling',
  'peiling-avstand': 'Peiling + avstand',
  frekvens: 'Frekvens',
  lydsignatur: 'Lydsignatur',
  bilde: 'Bilde',
};

export type Maaling =
  /** Retning uten avstand. Gir en LINJE, ikke et punkt — det er hele poenget. */
  | { slag: 'peiling'; peilingDeg: number; usikkerhetDeg?: number }
  /** Radarens måling: retning OG avstand. Gir et punkt. */
  | {
      slag: 'peiling-avstand';
      peilingDeg: number;
      usikkerhetDeg?: number;
      avstandM: number;
      usikkerhetM?: number;
    }
  /**
   * KrakenRF: senterfrekvens + (når antennerekka løser det) innkomstretning.
   * `peilingDeg` er valgfri fordi en RF-deteksjon uten DOA fortsatt er en
   * observasjon — den sier «noen sender på 156,8 MHz herfra», og det er verdt
   * å vise selv uten retning.
   */
  | {
      slag: 'frekvens';
      frekvensHz: number;
      bandbreddeHz?: number;
      /** Broens klassifisering («FM», «AIS», «DSSS»). Klartekst, ikke kode. */
      modulasjon?: string;
      signalDbm?: number;
      peilingDeg?: number;
      usikkerhetDeg?: number;
    }
  /** Hydrofon: navnet på signaturen broen kjente igjen, med SNR som kvalitet. */
  | {
      slag: 'lydsignatur';
      signaturNavn: string;
      snrDb?: number;
      peilingDeg?: number;
      usikkerhetDeg?: number;
    }
  /**
   * PTZ-kamera. `bildeUrl` er VALGFRI med vilje: broen kan melde at kameraet ser
   * kontakten (peiling + klassifisering) uten å ha lagt ut et stillbilde ennå —
   * bilder er tunge og hentes separat. En bilde-observasjon må likevel bære minst
   * én av `bildeUrl`/`peilingDeg`, ellers sier den ingenting (håndheves i
   * `api/kilde.ts`). Da kan videoruta si «kameraet ser kontakten, bilde ikke
   * levert» i stedet for å vise en ødelagt bilderamme.
   */
  | {
      slag: 'bilde';
      bildeUrl?: string;
      peilingDeg?: number;
      zoom?: number;
      /** Broens bildeklassifisering, om den har en. Klartekst. */
      klassifisering?: string;
    };

/**
 * Peilingen i en måling, om den har en. Alle fem variantene har feltet — to har
 * det som KRAV, tre som mulighet — så oppslaget er trygt for hele unionen. Det
 * ligger i en funksjon likevel, slik at en ny variant uten peiling tvinger en
 * endring HER framfor å gi `undefined` i stillhet et halvt dusin steder.
 */
export function maalingPeiling(m: Maaling): number | undefined {
  return m.peilingDeg;
}

/** Avstanden i en måling, om den har en. Bare radaren måler avstand. */
export function maalingAvstandM(m: Maaling): number | undefined {
  return m.slag === 'peiling-avstand' ? m.avstandM : undefined;
}

export interface Observasjon extends Ovelsemerket {
  id: string;
  sensorId: string;
  /** Kontakten broens fusjon knyttet observasjonen til. Mangler = uassosiert. */
  kontaktId?: string;
  tsMs: number;
  maaling: Maaling;
  /**
   * 0–1: hvor sikker MÅLINGEN er (SNR, antall treff, geometri) — IKKE hvor
   * sikker klassifiseringen av fartøyet er. De to blandes ofte sammen, og da
   * får en skarp peiling på et ukjent objekt samme tall som en vag peiling på
   * et kjent. Klassifiseringstillit hører på kontakten, ikke her.
   */
  sikkerhet?: number;
}

// ── Kontakten: det fusjonerte objektet operatøren jobber med ─────────────────

/**
 * Fartøyklasse. `usv` og `rib` står først fordi de er grunnen til at posten
 * finnes: en ubemannet overflatefarkost og en hurtiggående rib er små, raske og
 * dårlige radarmål, og de er den trusselen kystovervåkingen skal fange.
 */
export type Fartoyklasse = 'usv' | 'rib' | 'smaabaat' | 'storfartoy' | 'ukjent';

export const FARTOYKLASSE_NAVN: Record<Fartoyklasse, string> = {
  usv: 'USV (ubemannet)',
  rib: 'Hurtiggående rib',
  smaabaat: 'Småbåt',
  storfartoy: 'Større fartøy',
  ukjent: 'Uklassifisert',
};

/** Operatørens manuelle merking. Overstyrer alltid automatikken i sorteringen. */
export type Operatorflagg = 'ingen' | 'folg' | 'trussel' | 'avskrevet';

export interface Kystkontakt extends Ovelsemerket {
  id: string;
  postId: string;
  /** Operatørens eller broens navn på kontakten. Mangler = vis id-en. */
  navn?: string;
  tilhorighet: Affiliation;
  klasse: Fartoyklasse;
  lat?: number;
  lon?: number;
  /**
   * Er posisjonen MÅLT/krysspeilt, eller projisert fra én enkelt peiling?
   * Skillet må ut i UI-et: en projisert posisjon er en antakelse om avstand,
   * og å tegne den som et fast punkt er å lyve på kartet.
   */
  posisjonLost: boolean;
  /** Nyeste observasjonstidspunkt for kontakten. */
  sisteTsMs: number;
  fartKn?: number;
  kursDeg?: number;
  flagg: Operatorflagg;
  /** Alle observasjoner fusjonen har knyttet til kontakten, nyeste hvor som helst. */
  observasjoner: Observasjon[];
}

// ── Postens egen tilstand ───────────────────────────────────────────────────

export interface KopPost extends Ovelsemerket {
  id: string;
  navn: string;
  lat?: number;
  lon?: number;
  /** Hvilken SKADE-org posten meldes inn under — brukes til RBAC-scope. */
  distrikt?: string;
  omrade?: string;
}

// ── WS-konvolutt mot KOP-broen ──────────────────────────────────────────────

/** Kanalnavn på hubben. Speiler `RADAR_KANAL`-mønsteret i contracts/radar.ts. */
export const KOP_KANAL = 'kop' as const;

/**
 * `ovelse` står på KONVOLUTTEN og ikke bare på innholdet, fordi det er
 * konvolutten øvingskilden stempler (`lagOvelseskilde` — den ene utgangen). At
 * det er valgfritt betyr «ikke fra en øving», som er normaltilstanden: en ekte
 * bro kan ikke sette flagget, for `tolkMelding` under leser det aldri fra wire.
 */
export type KopMelding = Ovelsemerket & (
  | { type: 'post'; post: KopPost }
  | { type: 'sensor'; sensor: KopSensor }
  | { type: 'observasjon'; observasjon: Observasjon }
  | { type: 'kontakt'; kontakt: Kystkontakt }
  | { type: 'kontakt-borte'; kontaktId: string; arsak: string }
  | { type: 'sensor-borte'; sensorId: string; arsak: string }
);

/** Det NJORD sender TILBAKE. Alt her har en konsekvens i verden. */
export type KopKommando =
  /** Pek et PTZ-kamera mot en kontakt. Broen eier grensene, ikke klienten. */
  | { type: 'pekKamera'; sensorId: string; kontaktId: string }
  | { type: 'settFlagg'; kontaktId: string; flagg: Operatorflagg }
  /** Løft kontakten til en målløsning i SKADE. Se `lib/maalloesning.ts`. */
  | { type: 'maalloesning'; kontaktId: string; mgrs: string; begrunnelse: string };

// ── Ut mot SKADE: kontakten som kanonisk Track ───────────────────────────────

/**
 * SIDC for en kystkontakt på sjøoverflaten.
 *
 * Konvensjonen er ikke funnet opp her — den er den samme som `aisToTracks` i
 * `contracts/ais.ts` bruker (`S{tilhørighet}SP------`). Eierens krav er at
 * dataene skal kunne leses 1:1 i SKADE, og da må symbolet være identisk med
 * det COP allerede tegner for sjøoverflatespor. To ulike SIDC-serier for
 * samme slags objekt ville gitt to ulike symboler for én båt.
 */
const TILHORIGHET_TEGN: Record<Affiliation, string> = {
  friend: 'F',
  hostile: 'H',
  neutral: 'N',
  unknown: 'U',
};

export function kontaktSidc(tilhorighet: Affiliation): string {
  return `S${TILHORIGHET_TEGN[tilhorighet]}SP------`;
}

/**
 * Kontakt → kanonisk `Track`. DETTE er hvordan NJORDs bilde blir lesbart 1:1 i
 * SKADE: ingen egen NJORD-modell på kartet i COP, bare den vanlige `Track`.
 *
 * To felt bærer ærligheten videre til SKADE:
 *   * `estimated` settes når posisjonen IKKE er løst — COP tegner da punktet
 *     som estimert i stedet for målt.
 *   * `extra` bærer med seg hvor MANGE og hvilke sensorarter som ser kontakten,
 *     slik at en operatør i SKADE ser det samme skillet NJORD-operatøren ser:
 *     én sensor er en indikasjon, tre er et faktum.
 *
 * ══ EN ØVINGSKONTAKT BLIR ALDRI EN TRACK ═══════════════════════════════════
 * Funksjonen NEKTER for `k.ovelse`. Tidligere merket den bare sporet med
 * `extra.DEMO = 'IKKE EKTE DATA'`, og det var ikke godt nok: `extra` er en
 * detaljrute i et kort som må åpnes, mens sporet ligger på kartet i COP med
 * fullt symbol og full tilhørighet i det sekundet det er sendt. Et oppdiktet
 * fartøy i SKADEs situasjonsbilde er en falsk melding, uansett hvor mange
 * merkelapper det bærer. Dette er krav 4 fra `ovelse.ts` på utgangen som ikke
 * går gjennom kommandokøen.
 */
export function kontaktTilTrack(k: Kystkontakt, sensorer: readonly KopSensor[]): Track | null {
  if (k.ovelse) return null;
  if (k.lat === undefined || k.lon === undefined) return null;
  const artPerSensor = new Map(sensorer.map((s) => [s.id, s.art]));
  const arter = [
    ...new Set(
      k.observasjoner
        .map((o) => artPerSensor.get(o.sensorId))
        .filter((a): a is Sensorart => a !== undefined),
    ),
  ];
  const extra: Record<string, string> = {
    post: k.postId,
    klasse: FARTOYKLASSE_NAVN[k.klasse],
    sensorer: arter.length ? arter.map((a) => SENSORART_NAVN[a]).join(', ') : 'ingen',
    'antall sensorer': String(arter.length),
    posisjon: k.posisjonLost ? 'løst (krysspeilt/målt)' : 'projisert fra én peiling',
  };
  if (k.fartKn !== undefined) extra['fart'] = `${Math.round(k.fartKn)} kn`;
  if (k.kursDeg !== undefined) extra['kurs'] = `${Math.round(k.kursDeg)}°`;
  return {
    id: `kop:${k.postId}:${k.id}`,
    kind: 'detection',
    affiliation: k.tilhorighet,
    lat: k.lat,
    lon: k.lon,
    sidc: kontaktSidc(k.tilhorighet),
    label: k.navn || k.id,
    tsMs: k.sisteTsMs,
    source: `njord:${k.postId}`,
    estimated: !k.posisjonLost,
    extra,
  };
}
